/**
 * usePushNotifications
 * ====================
 * Gerencia a subscription Web Push do usuário.
 *
 *   const { permission, isSubscribed, loading, subscribe, unsubscribe } =
 *     usePushNotifications();
 *
 * NÃO pede permissão automaticamente — só quando o usuário chamar
 * `subscribe()`. Compatível com browsers que não têm Push API (degrada
 * graciosamente: isSupported = false).
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export function usePushNotifications() {
  const isSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  const [permission, setPermission] = useState(
    isSupported ? Notification.permission : 'unsupported'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isSupported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch (e) {
      setIsSubscribed(false);
    }
  }, [isSupported]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const subscribe = useCallback(async (municipioId = '') => {
    if (!isSupported) return false;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const { data: keyResp } = await api.get('/v2/push/vapid-public-key');
      const publicKey = keyResp.public_key;

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const rawP256 = subscription.getKey('p256dh');
      const rawAuth = subscription.getKey('auth');

      await api.post('/v2/push/subscribe', {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: bufferToBase64(rawP256),
          auth: bufferToBase64(rawAuth),
        },
        municipio_id: municipioId || localStorage.getItem('municipio_id') || '',
      });

      // Sincroniza a preferência server-side (silencioso — não bloqueia o subscribe)
      try {
        await api.put('/v2/notifications/settings', { push_notifications: true });
      } catch (e) {
        // ignore: preferência é complementar, subscription já foi salva
      }

      setIsSubscribed(true);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Push] subscribe failed:', e);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setIsSubscribed(false);
        return;
      }
      // Avisa o backend primeiro; depois unsubscribe local.
      try {
        await api.delete('/v2/push/unsubscribe', { data: { endpoint: sub.endpoint } });
      } catch (e) {
        // segue o unsubscribe local mesmo se backend falhar
      }
      // Atualiza preferência server-side
      try {
        await api.put('/v2/notifications/settings', { push_notifications: false });
      } catch (e) {
        // ignore
      }
      await sub.unsubscribe();
      setIsSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  return { isSupported, permission, isSubscribed, loading, subscribe, unsubscribe, refresh };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (c) => c.charCodeAt(0));
}

function bufferToBase64(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

export default usePushNotifications;
